'use client';

import React, { Component, ReactNode } from 'react';
import { logger } from '@/lib/logger';

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
		};
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {
			hasError: true,
			error,
		};
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		logger.error('ErrorBoundary caught an error', error, {
			componentStack: errorInfo.componentStack,
		});

		if (this.props.onError) {
			this.props.onError(error, errorInfo);
		}
	}

	handleReset = (): void => {
		this.setState({
			hasError: false,
			error: null,
		});
	};

	render(): ReactNode {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return (
				<div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
					<div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
						<h2 className="text-2xl font-bold text-red-600 mb-4">
							Une erreur est survenue
						</h2>
						<p className="text-gray-600 mb-6">
							{this.state.error?.message || 'Une erreur inattendue s\'est produite. Veuillez réessayer.'}
						</p>
						<div className="flex gap-4 justify-center">
							<button
								onClick={this.handleReset}
								className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
							>
								Réessayer
							</button>
							<button
								onClick={() => window.location.href = '/'}
								className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
							>
								{"Retour à l'accueil"}
							</button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

